import {Box, Button, Modal, Typography} from '@mui/material';
import React, {useCallback, useState, useEffect} from 'react';
import styles from '../../styles';
import {Control, FieldValues, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useAppDispatch} from '../../store/hooks';
import FormTextField from '../shared/FormTextField';
import {getDrillAsync, updateDrillAsync} from '../../store/drillSlice';
import {Drill} from '../../interfaces';

type DrillFormValues = {
  name: string;
  description: string;
};

interface Props {
  drill: Drill;
}

const EditDrillDialog: React.FC<Props> = ({drill}) => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const {control, handleSubmit, reset} = useForm<DrillFormValues>({
    defaultValues: {
      name: drill.name,
      description: drill.description ?? '',
    },
  });
  const [showEditDrillDialog, setShowEditDrillDialog] = useState(false);
  const [pending, setPending] = useState(false);

  // Reset form values when drill prop changes
  useEffect(() => {
    reset({
      name: drill.name,
      description: drill.description ?? '',
    });
  }, [drill, reset]);

  const handleEditDrillClose = useCallback(() => {
    reset();
    setShowEditDrillDialog(false);
  }, [reset]);

  const handleEditSubmit = useCallback(
    async (data: DrillFormValues) => {
      if (!drill.id) {
        console.error('Team ID is required');
        return;
      }
      setPending(true);
      await dispatch(
        updateDrillAsync({
          name: data.name,
          description: data.description,
          id: drill.id,
        }),
      );
      dispatch(getDrillAsync({id: drill.id}));
      setPending(false);
      handleEditDrillClose();
    },
    [dispatch, drill.id, handleEditDrillClose],
  );

  return (
    <>
      <Button
        variant="contained"
        disabled={pending}
        onClick={() => setShowEditDrillDialog(true)}>
        {t('library.editDrill')}
      </Button>
      <Modal
        open={showEditDrillDialog}
        onClose={handleEditDrillClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={styles.modal}>
          <Typography variant="h4" sx={{mb: 2}}>
            {t('library.editDrill')}
          </Typography>
          <form onSubmit={handleSubmit(handleEditSubmit)} className="form">
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="name"
              label={t('general.name') as string}
              required
            />
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="description"
              label={t('general.description') as string}
            />
            <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
              <Button variant="outlined" onClick={handleEditDrillClose}>
                {t('general.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={pending}
                sx={{ml: 2}}>
                {t('general.save')}
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default EditDrillDialog;
